import React, { useState, useRef } from 'react';
import { UploadType } from '../types';
import { X, Image as ImageIcon, Music as MusicIcon, Wand2, Loader2, Zap } from 'lucide-react';
import { generatePoeticCaption } from '../services/geminiService';
import { compressImage, getImageDimensions } from '../utils/imageCompression';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadPhoto: (file: File, caption: string) => Promise<void>;
  onUploadMusic: (file: File, title: string, artist: string) => Promise<void>;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUploadPhoto, onUploadMusic }) => {
  const [activeTab, setActiveTab] = useState<UploadType>(UploadType.PHOTO);
  const [file, setFile] = useState<File | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [compressionInfo, setCompressionInfo] = useState<{
    originalSize: number;
    compressedSize: number;
    originalDimensions: { width: number; height: number };
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setOriginalFile(selectedFile);
      
      if (activeTab === UploadType.PHOTO) {
        setIsCompressing(true);
        try {
          // 获取原始图片信息
          const originalDimensions = await getImageDimensions(selectedFile);
          const originalSize = selectedFile.size;
          
          console.log(`原始图片: ${(originalSize / 1024).toFixed(1)}KB, ${originalDimensions.width}x${originalDimensions.height}`);
          
          // 压缩图片
          const compressedFile = await compressImage(selectedFile, { 
            maxSizeKB: 30,
            maxWidth: 1920,
            maxHeight: 1080
          });
          
          setFile(compressedFile);
          setPreviewUrl(URL.createObjectURL(compressedFile));
          
          // 设置压缩信息
          setCompressionInfo({
            originalSize,
            compressedSize: compressedFile.size,
            originalDimensions
          });
          
          console.log(`压缩后: ${(compressedFile.size / 1024).toFixed(1)}KB`);
          
        } catch (error) {
          console.error('图片压缩失败:', error);
          // 如果压缩失败，使用原始文件
          setFile(selectedFile);
          setPreviewUrl(URL.createObjectURL(selectedFile));
          setCompressionInfo(null);
        } finally {
          setIsCompressing(false);
        }
      } else {
        // 音频文件不需要压缩
        setFile(selectedFile);
        setPreviewUrl(URL.createObjectURL(selectedFile));
      }
    }
  };

  const handleGenerateCaption = async () => {
    if (!originalFile || activeTab !== UploadType.PHOTO) return;

    setIsGenerating(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = (reader.result as string).split(',')[1];
        const generatedCaption = await generatePoeticCaption(base64String);
        setCaption(generatedCaption);
        setIsGenerating(false);
      };
      reader.readAsDataURL(originalFile);
    } catch (error) {
      console.error("Failed to process image", error);
      setIsGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      if (activeTab === UploadType.PHOTO) {
        await onUploadPhoto(file, caption || "未命名的瞬间");
      } else {
        await onUploadMusic(file, title || file.name, artist || "未知艺术家");
      }
      
      // Reset and close
      setFile(null);
      setOriginalFile(null);
      setPreviewUrl(null);
      setCaption('');
      setTitle('');
      setArtist('');
      setCompressionInfo(null);
      onClose();
    } catch (error) {
      console.error('上传失败:', error);
      setUploadError(error instanceof Error ? error.message : '上传失败，请重试');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_0.3s_ease-out]">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-stone-100">
          <h2 className="text-xl font-serif text-stone-800">添加到收藏</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800 transition">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-100">
          <button 
            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center space-x-2 transition ${activeTab === UploadType.PHOTO ? 'text-emerald-800 border-b-2 border-emerald-800 bg-stone-50' : 'text-stone-500 hover:text-stone-700'}`}
            onClick={() => { 
              setActiveTab(UploadType.PHOTO); 
              setFile(null); 
              setOriginalFile(null);
              setPreviewUrl(null); 
              setCompressionInfo(null);
              setUploadError(null);
            }}
          >
            <ImageIcon size={18} />
            <span>照片</span>
          </button>
          <button 
            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center space-x-2 transition ${activeTab === UploadType.MUSIC ? 'text-emerald-800 border-b-2 border-emerald-800 bg-stone-50' : 'text-stone-500 hover:text-stone-700'}`}
            onClick={() => { 
              setActiveTab(UploadType.MUSIC); 
              setFile(null); 
              setOriginalFile(null);
              setPreviewUrl(null); 
              setCompressionInfo(null);
              setUploadError(null);
            }}
          >
            <MusicIcon size={18} />
            <span>音乐</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* File Input */}
          <div className="border-2 border-dashed border-stone-200 rounded-xl p-8 text-center hover:border-emerald-500 transition cursor-pointer bg-stone-50" onClick={() => fileInputRef.current?.click()}>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept={activeTab === UploadType.PHOTO ? "image/*" : "audio/*"}
              onChange={handleFileChange}
            />
            
            {isCompressing ? (
              <div className="flex flex-col items-center text-emerald-600">
                <Zap size={48} className="mb-2 animate-pulse" />
                <p className="text-sm font-medium">正在压缩图片...</p>
                <div className="mt-2 w-32 h-1 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            ) : previewUrl && activeTab === UploadType.PHOTO ? (
               <div className="space-y-3">
                 <img src={previewUrl} alt="preview" className="max-h-48 mx-auto rounded-md shadow-md object-contain" />
                 {compressionInfo && (
                   <div className="text-xs text-stone-500 bg-stone-100 rounded-lg p-3 space-y-1">
                     <div className="flex justify-between">
                       <span>原始大小:</span>
                       <span className="font-medium">{(compressionInfo.originalSize / 1024).toFixed(1)} KB</span>
                     </div>
                     <div className="flex justify-between">
                       <span>压缩后:</span>
                       <span className="font-medium text-emerald-600">{(compressionInfo.compressedSize / 1024).toFixed(1)} KB</span>
                     </div>
                     <div className="flex justify-between">
                       <span>压缩率:</span>
                       <span className="font-medium text-emerald-600">
                         {((1 - compressionInfo.compressedSize / compressionInfo.originalSize) * 100).toFixed(1)}%
                       </span>
                     </div>
                     <div className="flex justify-between">
                       <span>原始尺寸:</span>
                       <span className="font-medium">{compressionInfo.originalDimensions.width}×{compressionInfo.originalDimensions.height}</span>
                     </div>
                   </div>
                 )}
               </div>
            ) : (
              <div className="flex flex-col items-center text-stone-400">
                {activeTab === UploadType.PHOTO ? <ImageIcon size={48} className="mb-2" /> : <MusicIcon size={48} className="mb-2" />}
                <p className="text-sm">点击上传{activeTab === UploadType.PHOTO ? '照片' : '音频文件'}</p>
                {activeTab === UploadType.PHOTO && (
                  <p className="text-xs text-stone-400 mt-1">图片将自动压缩至30KB以内</p>
                )}
                {file && <p className="text-emerald-600 font-medium mt-2">{file.name}</p>}
              </div>
            )}
          </div>

          {/* Metadata Inputs */}
          {activeTab === UploadType.PHOTO ? (
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-stone-500 font-bold">心情配文</label>
              <div className="relative">
                <textarea 
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="写下此刻的心情..."
                  className="w-full p-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-stone-700 font-serif resize-none h-24"
                />
                {previewUrl && (
                  <button 
                    onClick={handleGenerateCaption}
                    disabled={isGenerating}
                    className="absolute bottom-3 right-3 text-xs bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full flex items-center space-x-1 hover:bg-emerald-200 transition disabled:opacity-50"
                  >
                    {isGenerating ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12} />}
                    <span>AI 灵感</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
             <div className="space-y-4">
               <div>
                  <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">歌曲标题</label>
                  <input 
                    type="text" 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="例如：有没有人曾告诉你"
                  />
               </div>
               <div>
                  <label className="block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">歌手</label>
                  <input 
                    type="text" 
                    value={artist}
                    onChange={(e) => setArtist(e.target.value)}
                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="例如：陈楚生"
                  />
               </div>
             </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              <div className="flex items-center space-x-2">
                <X size={16} className="text-red-500" />
                <span>{uploadError}</span>
              </div>
            </div>
          )}

          <button 
            onClick={handleSubmit}
            disabled={!file || isCompressing || isUploading}
            className="w-full py-3 bg-stone-800 text-stone-50 rounded-lg font-medium hover:bg-stone-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {isUploading ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 size={16} className="animate-spin" />
                <span>上传中...</span>
              </span>
            ) : isCompressing ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 size={16} className="animate-spin" />
                <span>压缩中...</span>
              </span>
            ) : (
              activeTab === UploadType.PHOTO ? '发布照片' : '添加音乐'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;